import { ListDataProps } from 'store/interfaces';

export type AdvisorStatus =
  | 'pending'
  | 'applied'
  | 'ignored'
  | 'expired'
  | 'reverted';

type AdvisorOperationMode = 'manual' | 'period' | 'micro' | 'spot_period';

type PersonaAction = {
  operator_uuid: string;
  first_name: string;
  last_name: string;
  email: string;
};

export enum AdvisorTypeEnum {
  WEEKDAY = 'empty_instances_weekday',
  SUNDAY = 'empty_instances_sunday',
  SATURDAY = 'empty_instances_saturday',
  ANYDAY = 'empty_instances_any_day',
  DYNAMIC_WEEKDAY_START = 'dynamic_advisor_weekday_start',
  DYNAMIC_WEEKDAY_END = 'dynamic_advisor_weekday_end',
}

interface AdvisorEnvironment {
  environment_uuid: string;
  environment_name: string;
  environment_type: AdvisorOperationMode;
  environment_provider: string;
  environment_licenses: number;
  environment_timezone: string;
  environment_should_apply_advisor_automatically: boolean;
}

export interface AdvisorProps {
  advisor_uuid: string;
  advisor_type: AdvisorTypeEnum;
  impact: 'low' | 'medium' | 'high';
  company: { company_uuid: string; company_name: string };
  partner: { partner_uuid: string; partner_name: string };
  environment: AdvisorEnvironment;
  created_at: string;
  updated_at: string | null;
  updated_by: PersonaAction | null;
  status: AdvisorStatus;
  estimated_monthly_cost_savings: string | null;
  estimated_annual_cost_savings: string | null;
  applied_at: Date;
  applied_by: PersonaAction;
  applied_automatically: boolean;
  ignored_at: Date;
  ignored_by: PersonaAction;
  ignored_reason: string;
  reverted_at: Date;
  reverted_by: PersonaAction;
}

interface PendingAdvisorDetails {
  status: 'pending';
  environment: {
    behavior: {
      current: AdvisorEnvironmentBehavior;
      suggested: AdvisorEnvironmentBehavior;
    };
  };
}

interface AppliedAdvisorDetails {
  status: 'applied';
  environment: {
    behavior: {
      previous: AdvisorEnvironmentBehavior;
      applied: AdvisorEnvironmentBehavior;
    };
  };
}

interface IgnoredAdvisorDetails {
  status: 'ignored';
}

interface ExpiredAdvisorDetails {
  status: 'expired';
}

interface RevertedAdvisorDetails {
  status: 'reverted';
  environment: {
    behavior: {
      previous: AdvisorEnvironmentBehavior;
      applied: AdvisorEnvironmentBehavior;
    };
  };
}

interface BaseAdvisorDetails extends AdvisorProps {
  environment: AdvisorEnvironment;
}

export type AdvisorDetailsProps =
  | (BaseAdvisorDetails & PendingAdvisorDetails)
  | (BaseAdvisorDetails & AppliedAdvisorDetails)
  | (BaseAdvisorDetails & IgnoredAdvisorDetails)
  | (BaseAdvisorDetails & ExpiredAdvisorDetails)
  | (BaseAdvisorDetails & RevertedAdvisorDetails);

export interface AdvisorEnvironmentBehaviorPeriod {
  active: boolean;
  hour: string;
  scale_instances_by_licences_margin: boolean;
  licences_margin_percent: number;
  application_server_mode: 'activated' | 'stopped' | 'on_demand';
}

export interface AdvisorEnvironmentBehavior {
  week: {
    first_period: AdvisorEnvironmentBehaviorPeriod;
    second_period: AdvisorEnvironmentBehaviorPeriod;
  };
  saturday: {
    first_period: AdvisorEnvironmentBehaviorPeriod;
    second_period: AdvisorEnvironmentBehaviorPeriod;
  };
  sunday: {
    first_period: AdvisorEnvironmentBehaviorPeriod;
    second_period: AdvisorEnvironmentBehaviorPeriod;
  };
}

export interface ListAdvisorsDataProps extends ListDataProps {
  results: AdvisorProps[];
}

export interface DashboardProps {
  advisors_count: number;
  environments_with_advisors_count: number;
  environments_with_advisors_percent: number;
  advisors_last_30_days_applied_count: number;
  advisors_last_30_days_applied_percent: number;
  pending_advisors_monthly_estimated_cost_savings: string;
  pending_advisors_annual_estimated_cost_savings: string;
}

export interface AdvisorsState {
  readonly advisorDetails: AdvisorDetailsProps | null;
  readonly listAdvisors: ListAdvisorsDataProps;
  readonly dashboard: DashboardProps | null;
  readonly isLoadingDashboard: boolean;
  readonly isLoading: boolean;
}
